import React, { useState, useEffect } from 'react';
import { 
  Trophy, Users, Calendar, FileText, Award, ClipboardList,
  TrendingUp, AlertCircle, CheckCircle, Clock, Target
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
         PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import api from '../api';

const ConcoursDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Chargement des données du dashboard...');
      
      const [statsResponse, sessionsResponse] = await Promise.all([
        api.get('/concours/statistiques/'),
        api.get('/concours/sessions/')
      ]);
      
      console.log('📊 Réponse statistiques:', statsResponse.data);
      console.log('📋 Réponse sessions:', sessionsResponse.data);
      
      // Correction: utiliser statsResponse.data.data au lieu de statsResponse.data
      const statsData = statsResponse.data?.data || statsResponse.data || {};
      const sessionsData = sessionsResponse.data?.data || sessionsResponse.data?.results || sessionsResponse.data || [];
      
      console.log('✅ Données statistiques traitées:', statsData);
      console.log('✅ Données sessions traitées:', sessionsData);
      
      setStats(statsData);
      setSessions(Array.isArray(sessionsData) ? sessionsData : []);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des données:', error);
      console.error('❌ Détails de l\'erreur:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // Données pour les graphiques
  const pieData = [
    { name: 'Brouillon', value: stats.repartition_statuts?.BROUILLON || 0, color: '#F59E0B' },
    { name: 'Confirmé', value: stats.repartition_statuts?.CONFIRME || 0, color: '#10B981' },
    { name: 'Soumis', value: stats.repartition_statuts?.SOUMIS || 0, color: '#3B82F6' },
    { name: 'Rejeté', value: stats.repartition_statuts?.REJETE || 0, color: '#EF4444' }
  ];

  const sexeData = [
    { name: 'Masculin', value: stats.repartition_sexe?.M || 0, color: '#3B82F6' },
    { name: 'Féminin', value: stats.repartition_sexe?.F || 0, color: '#EC4899' }
  ];

  const ageData = Object.entries(stats.repartition_age || {}).map(([age, count]) => ({
    age,
    candidats: count
  }));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg shadow border border-blue-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-500 mr-4">
              <Calendar className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-800">Total Sessions</h3>
              <p className="text-2xl font-bold text-blue-900">{stats.total_sessions || 0}</p>
              <p className="text-xs text-blue-600 mt-1">{stats.sessions_actives || 0} active(s)</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg shadow border border-green-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-500 mr-4">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-green-800">Total Candidats</h3>
              <p className="text-2xl font-bold text-green-900">{stats.total_candidats || 0}</p>
              <p className="text-xs text-green-600 mt-1">{stats.candidats_admis || 0} admis</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg shadow border border-purple-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-500 mr-4">
              <Trophy className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-purple-800">Taux de Réussite</h3>
              <p className="text-2xl font-bold text-purple-900">{stats.taux_reussite_global || 0}%</p>
              <p className="text-xs text-purple-600 mt-1">Global</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-lg shadow border border-orange-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-500 mr-4">
              <Target className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-orange-800">Moyenne Générale</h3>
              <p className="text-2xl font-bold text-orange-900">{stats.moyenne_notes || 0}/20</p>
              <p className="text-xs text-orange-600 mt-1">Toutes sessions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Répartition par statut */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par Statut</h3>
          {pieData.some(item => item.value > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({percent}) => `${(percent * 100).toFixed(1)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Aucun candidat enregistré</p>
            </div>
          )}
        </div>

        {/* Répartition par sexe */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par Sexe</h3>
          {sexeData.some(item => item.value > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={sexeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({percent}) => `${(percent * 100).toFixed(1)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {sexeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Aucune donnée disponible</p>
            </div>
          )}
        </div>
      </div>

      {/* Répartition par âge */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par Tranche d'Âge</h3>
        {ageData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ageData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="age" 
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: '#e0e0e0' }}
                tickLine={{ stroke: '#e0e0e0' }}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: '#e0e0e0' }}
                tickLine={{ stroke: '#e0e0e0' }}
              />
              <Tooltip 
                formatter={(value) => [value, 'Candidats']}
                labelFormatter={(label) => `Âge: ${label}`}
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Bar 
                dataKey="candidats" 
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <BarChart className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Aucune donnée disponible</p>
          </div>
        )}
      </div>

      {/* Sessions récentes */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sessions Récentes</h3>
        {sessions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Session
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidats
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Places
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sessions.slice(0, 5).map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {session.nom}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.type_concours?.nom}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        session.statut === 'INSCRIPTION_OUVERTE' ? 'bg-green-100 text-green-800' :
                        session.statut === 'EN_COURS' ? 'bg-blue-100 text-blue-800' :
                        session.statut === 'TERMINE' ? 'bg-gray-100 text-gray-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {session.statut_display}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.nombre_inscrits || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.nombre_places_disponibles}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Aucune session enregistrée</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConcoursDashboard; 